import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ArrowRight, Link, Star, Code, Users } from '@phosphor-icons/react';
import iotDashboard from '../assets/images/iot-dashboard.png';
import dossierWebsite from '../assets/images/dossier-website.png';

import elevateOxygen from '../assets/images/elevate_oxygen.webp';
import vaneschWebsite from '../assets/images/vanesch_boomkwekerij.webp';
import techStack from '../assets/images/tech-stack.jpg';
import qleaner from '../assets/images/qleaner.webp';
import leefPosters from '../assets/images/leef_posters.webp';
import shareboatIcon from '../assets/images/apps/shareboat-icon.png';
import toepenIcon from '../assets/images/apps/toepen-icon.png';
import stridesIcon from '../assets/images/apps/strides-icon.png';

const PortfolioPage = () => {
  const { t } = useTranslation();

  // Portfolio statistics to show scale
  const portfolioStats = {
    totalProjects: 50,
    shownProjects: 12,
    categories: 8,
    yearsExperience: 5
  };

  // All projects mixed together - no categories
  const allProjects = [
    {
      id: 'vanesch',
      title: 'Van Esch Boomkwekerij Website',
      description: t('portfolio.projects.vanesch.description'),
      technologies: ['WordPress', 'PHP', 'Office 365'],
      image: vaneschWebsite,
      liveUrl: 'https://vaneschboomkwekerij.nl/',
      category: 'Web Development',
      featured: true
    },
    {
      id: 'dalys',
      title: 'Dalys Marketing Website',
      description: t('portfolio.projects.dalys.description'),
      technologies: ['React'],
      image: iotDashboard,
      liveUrl: 'https://dalys.ai',
      category: 'Web Development',
      featured: true
    },
    {
      id: 'dalys_iot',
      title: 'Dalys IoT Dashboard',
      description: t('portfolio.projects.dalys.description'),
      technologies: ['Ruby on Rails', 'MQTT', 'IoT APIs'],
      image: iotDashboard,
      category: 'Enterprise Dashboard',
      featured: true
    },
    {
      id: 'dalys_vision',
      title: 'Dalys Vision website',
      description: t('portfolio.projects.dalys.description'),
      technologies: ['Wordpress'],
      image: iotDashboard,
      category: 'Enterprise Dashboard',
      liveUrl: 'https://vision.dalys.ai',
      featured: true
    },
    {
      id: 'elevate',
      title: 'Elevate Oxygen Webshop',
      description: t('portfolio.projects.elevate.description'),
      technologies: ['Wordpress', 'Woocommerce'],
      image: elevateOxygen,
      liveUrl: 'https://elevateoxygen.nl',
      category: 'E-commerce',
      featured: true
    },
    {
      id: 'leefposters',
      title: 'Leefposters.nl',
      description: t('portfolio.projects.leefposters.description'),
      technologies: ['Wordpress', 'Woocommerce', 'Mollie'],
      image: leefPosters,
      liveUrl: 'https://leefposters.nl',
      category: 'E-commerce',
      featured: true
    },
    {
      id: 'dossier',
      title: 'Dossier Arbeidsmigranten Blog',
      description: t('portfolio.projects.dossier.description'),
      technologies: ['WordPress'],
      image: dossierWebsite,
      liveUrl: 'https://dossierarbeidsmigranten.nl',
      category: 'Web Development',
      featured: true
    },
    {
      id: 'qleaner',
      title: 'Qleaner Webshop',
      description: t('portfolio.projects.qleaner.description'),
      technologies: ['Wordpress', 'Woocommerce', 'Mollie'],
      image: qleaner,
      liveUrl: 'https://www.qleaner.nl',
      category: 'E-commerce',
      featured: false
    },
    {
      id: 'toepen',
      title: 'Toepen Mobiele App',
      description: t('portfolio.projects.toepen.description'),
      technologies: ['React Native', 'Supabase'],
      image: toepenIcon,
      liveUrl: 'https://apps.apple.com/nl/app/toep-scorekaart/id6636478336?l=en-GB',
      category: 'Mobiele App',
      featured: false
    },
    {
      id: 'ARK',
      title: 'ARK Real-Time Nature tracking',
      description: 'IoT tracking dashboard voor ARK IoT tracking system',
      technologies: ['Rails', 'IoT', 'Python', 'PostgreSQL', 'SenseCAP'],
      image: techStack,
      category: 'Enterprise Dashboard',
      featured: false
    },
    {
      id: 'vlb',
      title: 'VLB Online Environment',
      description: t('portfolio.projects.mosae.description'),
      technologies: ['Infrastructure', 'Monitoring', 'Support'],
      image: techStack,
      category: 'IT Infrastructure',
      featured: false
    },
    {
      id: 'mosae',
      title: 'Mosae IT Infrastructure',
      description: t('portfolio.projects.mosae.description'),
      technologies: ['Infrastructure', 'Monitoring', 'Support'],
      image: techStack,
      category: 'IT Infrastructure',
      featured: false
    },
    {
      id: 'shareboat',
      title: 'Deel je Bootje Mobiele App',
      description: t('portfolio.projects.shareboat.description'),
      technologies: ['React Native', 'Firebase', 'Maps API'],
      image: shareboatIcon,
      liveUrl: 'https://apps.apple.com/nl/app/bootje-delen/id6502918546?l=en-GB',
      category: 'Mobiele App',
      featured: false
    },
    {
      id: 'strides',
      title: 'Strides Fitness App',
      description: 'Fitness tracking app met AI-gestuurde workout aanbevelingen en sociale features voor motivatie.',
      technologies: ['React Native', 'AI/ML', 'HealthKit'],
      image: stridesIcon,
      category: 'Mobiele App',
      featured: false
    }
  ];

  // Show all projects
  const featuredProjects = allProjects;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section with Portfolio Stats */}
      <section className="bg-gradient-to-br from-gray-900 via-blue-900 to-gray-800 py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="w-full h-full" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='4'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}></div>
        </div>
        <div className="container mx-auto px-4 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              {t('portfolio.hero.title')}
            </h1>
            <p className="text-xl text-gray-300 mb-12 max-w-2xl mx-auto">
              {t('portfolio.hero.subtitle')}
            </p>
            
            {/* Portfolio Statistics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="text-center"
              >
                <div className="text-3xl md:text-4xl font-bold text-blue-400 mb-2">{portfolioStats.totalProjects}+</div>
                <div className="text-gray-300 text-sm">Projecten Voltooid</div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="text-center"
              >
                <div className="text-3xl md:text-4xl font-bold text-green-400 mb-2">{portfolioStats.categories}</div>
                <div className="text-gray-300 text-sm">Specialisaties</div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="text-center"
              >
                <div className="text-3xl md:text-4xl font-bold text-blue-400 mb-2">{portfolioStats.yearsExperience}+</div>
                <div className="text-gray-300 text-sm">Jaar Ervaring</div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
                className="text-center"
              >
                <div className="text-3xl md:text-4xl font-bold text-yellow-400 mb-2">25+</div>
                <div className="text-gray-300 text-sm">Tevreden Klanten</div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Curated Selection Notice */}
      <section className="py-16 bg-white relative">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center max-w-4xl mx-auto"
          >
            <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Star className="ph-thin" size={16} />
              Portfolio Showcase
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('portfolioPage.recentProjects.title')}
            </h2>
            <p className="text-lg text-gray-600 mb-8 max-w-3xl mx-auto">
              {t('portfolioPage.recentProjects.subtitle')}
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <div className="flex items-center gap-2 text-gray-500">
                <Code className="ph-thin" size={20} />
                <span>{t('portfolioPage.recentProjects.technologies')}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-500">
                <Users className="ph-thin" size={20} />
                <span>{t('portfolioPage.recentProjects.industries')}</span>
              </div>
            </div>
          </motion.div>
        </div>
        
        {/* Background decoration */}
                        <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-blue-500 via-blue-500 to-blue-500"></div>
      </section>

      {/* Featured Projects Showcase */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Uitgelichte Projecten
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Een selectie van onze meest recente projecten
            </p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {featuredProjects.map((project, index) => (
              <motion.div
                key={project.id}
                variants={itemVariants}
                className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 group"
              >
                <div className="relative">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute top-4 right-4 flex gap-2">
                    {project.liveUrl && (
                      <a
                        href={project.liveUrl}
                        className="bg-white/90 backdrop-blur-sm p-2 rounded-full hover:bg-white transition-colors duration-300 shadow-lg"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Link size={20} className="text-gray-700" />
                      </a>
                    )}
                  </div>
                  
                  {/* Category badge */}
                  <div className="absolute top-4 left-4">
                    <div className="bg-black/70 backdrop-blur-sm text-white px-3 py-1 rounded-full text-xs font-medium">
                      {project.category}
                    </div>
                  </div>
                  
                  {/* Project complexity indicator */}
                  <div className="absolute bottom-4 left-4">
                    <div className="bg-black/70 backdrop-blur-sm text-white px-2 py-1 rounded text-xs">
                      {project.technologies.length} technologieën
                    </div>
                  </div>
                </div>
                
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">
                    {project.title}
                  </h3>
                  
                  <div className="mb-4 relative overflow-hidden">
                    <div className="transition-all duration-700 ease-out max-h-20 group-hover:max-h-96">
                      <p className="text-gray-600 leading-relaxed">
                        {project.description}
                      </p>
                    </div>
                    {/* Gradient fade for truncated text */}
                    <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-white via-white/80 to-transparent opacity-100 group-hover:opacity-0 transition-opacity duration-500 pointer-events-none"></div>
                  </div>
                  
                  <div className="flex flex-wrap gap-2 mb-4">
                    {project.technologies.slice(0, 3).map((tech) => (
                      <span
                        key={tech}
                        className="text-xs bg-gradient-to-r from-gray-100 to-gray-50 text-gray-600 px-3 py-1 rounded-full font-medium"
                      >
                        {tech}
                      </span>
                    ))}
                    {project.technologies.length > 3 && (
                      <span className="text-xs text-gray-400 px-2 py-1">
                        +{project.technologies.length - 3} meer
                      </span>
                    )}
                  </div>
                  
                  {/* Project status/badge */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {project.liveUrl && project.liveUrl !== '#' ? (
                        <span className="inline-flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full">
                          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                          Live
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded-full">
                          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                          Privé
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
          
        </div>
      </section>
    </div>
  );
};

export default PortfolioPage;
